import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from "reactstrap";
import CollapsablePanel from "../../common/collapsable-panel/collapsable-panel";
import { IOrg } from "../../../types/org";
import { useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import Spacer from "../../common/spacer";
import { AddIcon, DeleteIcon } from "../../common/icons";
import { DELETE_ORG_ADDRESSES, DELETE_ORG_CONTACT_PHONES, DELETE_ORG_DIGITAL_ADDRESSES, GET_ORG_ADDRESSES, GET_ORG_CONTACT_PHONES, GET_ORG_DIGITAL_ADDRESSES } from "../../../graphql/contact-gql";
import OrgPhone from "./org-phone/org-phone";
import OrgDigitalAddress from "./org-digital-address/org-digital-address";
import useToggle from "../../../hook/useToggle";
import OrgAddress from "./org-address/org-address";
import useCheckGroup from "../../../hook/useCheckGroup";
import SpinnerOverlay from "../../common/spinner/spinner";
import confirm from "../../common/confirm";
import { notifySuccess } from "../../../services/notification";
import PhoneCreate from "./org-phone/phone-create";
import AddressCreate from "./org-address/address-create";
import DigitalAddressCreate from "./org-digital-address/digital-address-create";
import { warn } from "../../../services/env";
import useAuth from "../../../hook/useAuth";

type ContactInfoProps = {
    org: IOrg
}
const ContactInfo = (props: ContactInfoProps) => {
    const {org} = props
    const { isFrenchTranslator } = useAuth()
    const [deleteOrgAddresses, {loading: delAddressLoading}] = useMutation(DELETE_ORG_ADDRESSES);
    const [deleteOrgPhones, {loading: delPhoneLoading}] = useMutation(DELETE_ORG_CONTACT_PHONES);
    const [deleteOrgDigital, {loading: delDigitalLoading}] = useMutation(DELETE_ORG_DIGITAL_ADDRESSES);

    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const toggleDropdownOpen = useCallback(() => {
        setDropdownOpen(!dropdownOpen)
    }, [dropdownOpen])

    const {isOpen: addressAddOpen, toggle: toggleAddressAdd} = useToggle()
    const {isOpen: contactPhoneAddOpen, toggle: toggleContactPhoneAdd} = useToggle()
    const {isOpen: digitalAddressAddOpen, toggle: toggleDigitalAddressAdd} = useToggle()

    const {checkGroup: addressCheckGroup} = useCheckGroup<number>()
    const {checkGroup: phoneCheckGroup} = useCheckGroup<number>()
    const {checkGroup: digitalAddressCheckGroup} = useCheckGroup<number>()

    const isNoneSelected = (): boolean => {
        return addressCheckGroup.getCheckedValues()?.length === 0
            && phoneCheckGroup.getCheckedValues()?.length === 0
            && digitalAddressCheckGroup.getCheckedValues()?.length === 0
    }

    const isCrossSectionSelected = (): boolean => {
        return addressCheckGroup.getCheckedValues()?.length > 0 && (phoneCheckGroup.getCheckedValues()?.length > 0 || digitalAddressCheckGroup.getCheckedValues()?.length > 0)
            || phoneCheckGroup.getCheckedValues()?.length > 0 && digitalAddressCheckGroup.getCheckedValues()?.length > 0
    }

    const removeContacts = () => {
        if (isCrossSectionSelected()) {
            warn({
                title: "Warning!",
                content: "You can only select/remove entries from one module at a time<br/> (e.g. Organization Addresses Information or Organization Phone Numbers or Organization Digital Addresses)."
            })
        } else {
            if (addressCheckGroup.getCheckedValues()?.length > 0) {
                removeAddress()
            } else if (phoneCheckGroup.getCheckedValues()?.length > 0) {
                removePhone()
            } else if (digitalAddressCheckGroup.getCheckedValues()?.length > 0) {
                removeDigitalAddress()
            }
        }
    }

    const removeAddress = () => {
        confirm({
            title: 'Remove Organization Addresses?',
            content: 'The selected organization addresses will be removed. <br/>Are you sure you want to continue?',
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            deleteOrgAddresses({
                variables: {
                    orgId: org.id,
                    addressUsageIds: addressCheckGroup.getCheckedValues()
                },
                refetchQueries: [{
                    query: GET_ORG_ADDRESSES,
                    variables: { orgId: org.id }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    const removePhone = () => {
        confirm({
            title: 'Remove Organization Phone Numbers?',
            content: 'The selected organization phone numbers will be removed. <br/>Are you sure you want to continue?',
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            deleteOrgPhones({
                variables: {
                    orgId: org.id,
                    contactPhoneIds: phoneCheckGroup.getCheckedValues()
                },
                refetchQueries: [{
                    query: GET_ORG_CONTACT_PHONES,
                    variables: { orgId: org.id }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    const removeDigitalAddress = () => {
        confirm({
            title: 'Remove Organization Digital Addresses?',
            content: 'The selected organization digital addresses will be removed. <br/>Are you sure you want to continue?',
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            deleteOrgDigital({
                variables: {
                    orgId: org.id,
                    digitalAddressIds: digitalAddressCheckGroup.getCheckedValues()
                },
                refetchQueries: [{
                    query: GET_ORG_DIGITAL_ADDRESSES,
                    variables: { orgId: org.id }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    return (
        <div>
            {(delAddressLoading || delPhoneLoading ||  delDigitalLoading) && <SpinnerOverlay />}

           {!isFrenchTranslator && <div className="pb-3">
                <ButtonDropdown isOpen={dropdownOpen} toggle={toggleDropdownOpen} style={{ marginRight: '15px' }} disabled={!!org.details?.shareLinkId}>
                    <DropdownToggle caret color="primary" disabled={!!org.details?.shareLinkId}>
                        <AddIcon/> Add
                    </DropdownToggle>
                    <DropdownMenu style={{ overflowY: 'auto' }}>
                        <DropdownItem onClick={toggleAddressAdd}>Organization Address</DropdownItem>
                        <DropdownItem onClick={toggleContactPhoneAdd}>Organization Phone Number</DropdownItem>
                        <DropdownItem onClick={toggleDigitalAddressAdd}>Organization Digital Address</DropdownItem>
                    </DropdownMenu>
                </ButtonDropdown>
                <Button color="danger" style={{ marginRight: '15px' }} onClick={removeContacts} disabled={isNoneSelected()||!!org.details?.shareLinkId}>
                    <DeleteIcon/> Remove
                </Button>
            </div>}

            <CollapsablePanel headTitle="Organization Addresses Information" initCollapse={false} helpUrl="">
                <OrgAddress org={org} checkGroup={addressCheckGroup}/>
            </CollapsablePanel>

            <Spacer/>
            <CollapsablePanel headTitle="Organization Phone Numbers" initCollapse={false} helpUrl="">
                <OrgPhone org={org} checkGroup={phoneCheckGroup}/>
            </CollapsablePanel>

            <Spacer/>
            <CollapsablePanel headTitle="Organization Digital Addresses" initCollapse={false} helpUrl="">
                <OrgDigitalAddress org={org} checkGroup={digitalAddressCheckGroup}/>
            </CollapsablePanel>

            <AddressCreate orgId={org?.id} isOpen={addressAddOpen} toggle={toggleAddressAdd}/>
            <PhoneCreate orgId={org?.id} isOpen={contactPhoneAddOpen} toggle={toggleContactPhoneAdd}/>
            <DigitalAddressCreate orgId={org?.id} isOpen={digitalAddressAddOpen} toggle={toggleDigitalAddressAdd}/>

        </div>
    )
}

export default ContactInfo
